import {
  GrowthWheelAssessmentComparisonCompetency,
  GrowthWheelAssessmentSession,
} from '../../types/GrowthWheel'

export type SvgPoint = {
  x: number
  y: number
}

export type ChartPoint = SvgPoint & {
  score: number
  competencyName: string
  sessionName: string
}

export const COLORS = ['#1F0E38', '#2A9D8F', '#E9C46A']
export const CENTER = 180
export const RADIUS = 108
export const MAX_SCORE = 5
export const LABEL_DISTANCE = RADIUS + 38
export const LEVELS = [1, 2, 3, 4, 5]

export const getPoint = (index: number, total: number, score: number) => {
  const angle = -Math.PI / 2 + (index * Math.PI * 2) / total
  const distance =
    (Math.max(0, Math.min(score, MAX_SCORE)) / MAX_SCORE) * RADIUS

  return {
    x: CENTER + Math.cos(angle) * distance,
    y: CENTER + Math.sin(angle) * distance,
  }
}

export const getAxisPoint = (
  index: number,
  total: number,
  distance = RADIUS,
) => {
  const angle = -Math.PI / 2 + (index * Math.PI * 2) / total

  return {
    x: CENTER + Math.cos(angle) * distance,
    y: CENTER + Math.sin(angle) * distance,
  }
}

export const buildSvgPoints = (points: SvgPoint[]) =>
  points.map(point => `${point.x},${point.y}`).join(' ')

export const wrapLabel = (label: string, maxLineLength = 16) => {
  const words = label.split(' ')
  const lines = words.reduce<string[]>((wrappedLines, word) => {
    const currentLine = wrappedLines[wrappedLines.length - 1]

    if (!currentLine) return [word]
    if (`${currentLine} ${word}`.length <= maxLineLength) {
      return [...wrappedLines.slice(0, -1), `${currentLine} ${word}`]
    }

    return [...wrappedLines, word]
  }, [])

  return lines.flatMap(line => {
    if (line.length <= maxLineLength) return [line]
    const chunks = line.match(new RegExp(`.{1,${maxLineLength}}`, 'g'))
    return chunks ?? [line]
  })
}

export const getComparisonSessions = (
  sessions: GrowthWheelAssessmentSession[],
) =>
  [...sessions]
    .filter(
      session =>
        session.status === 'completed' ||
        (session.status === 'in_progress' &&
          session.growthWheelStatus === 'active'),
    )
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))

export const getInitialSessionIds = (
  sessions: GrowthWheelAssessmentSession[],
  defaultSessionId: number | null,
) => {
  const defaultSession = sessions.find(
    session => session.id === defaultSessionId,
  )
  return defaultSession
    ? [defaultSession.id]
    : sessions[0]
    ? [sessions[0].id]
    : []
}

export const buildPointGroups = (
  sessions: GrowthWheelAssessmentSession[],
  competencies: GrowthWheelAssessmentComparisonCompetency[],
) =>
  sessions.map(session =>
    competencies.map((competency, competencyIndex) => {
      const score = competency.scores[session.id] ?? 0

      return {
        ...getPoint(competencyIndex, competencies.length, score),
        score,
        competencyName: competency.name,
        sessionName: session.name,
      }
    }),
  )
